import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from './header';
import Sidebar from './sidebar';
import { Table, Button, Input, Select } from 'antd';
import { Link, useNavigate } from 'react-router-dom';

const { Option } = Select;

const PlanningDocumentList = () => {
  const navigate = useNavigate();
  const [planningDocuments, setPlanningDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [size, setSize] = useState(20);

  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchField, setSearchField] = useState('');
  const [fiscalYear, setFiscalYear] = useState('all');
  const [fiscalYears, setFiscalYears] = useState([]);

  const FILTER_API_URL = 'https://ward3-chandrapur-api.palikaapp.com/api/planning/filter-by-fiscal-year';
  const ALL_API_URL = 'https://ward3-chandrapur-api.palikaapp.com/api/planning/pagination';
  const SEARCH_API_URL = 'https://ward3-chandrapur-api.palikaapp.com/api/planning/search';
  const FISCAL_YEAR_API_URL = 'https://ward3-chandrapur-api.palikaapp.com/api/fiscal-year/all';

  const fetchPlanningDocuments = async () => {
    try {
      let apiUrl = fiscalYear === 'all' ? ALL_API_URL : FILTER_API_URL;

      const params = {
        page: page,
        size: size,
        sortBy: sortBy,
        sortOrder: sortOrder,
      };

      if (fiscalYear && fiscalYear !== 'all') {
        params.fiscalYear = fiscalYear;
      }

      const response = await axios.get(apiUrl, { params });
      setPlanningDocuments(response.data.content || response.data);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching planning documents:', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const searchPlanningDocuments = async () => {
    try {
      const response = await axios.get(SEARCH_API_URL, {
        params: {
          keyword: searchKeyword,
          searchField: searchField
        }
      });
      setPlanningDocuments(response.data);
      setTotalPages(1);
    } catch (error) {
      console.error('Error searching planning documents:', error);
      setError(error);
    }
  };

  const fetchFiscalYears = async () => {
    try {
      const response = await axios.get(FISCAL_YEAR_API_URL);
      const allFiscalYears = [{ id: 'all', name: 'All' }, ...response.data];
      setFiscalYears(allFiscalYears);
    } catch (error) {
      console.error('Error fetching fiscal years:', error);
      setError(error);
    }
  };

  useEffect(() => {
    fetchFiscalYears();
  }, []);

  useEffect(() => {
    fetchPlanningDocuments();
  }, [page, size, sortBy, sortOrder, fiscalYear]);

  if (error) {
    return <div className="p-4 text-red-500">Error fetching planning documents: {error.message}</div>;
  }

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setPage(newPage);
    }
  };

  const columns = [
    {
      title: 'क्र.सं.',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'सिर्षक',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'रकम',
      dataIndex: 'budget',
      key: 'budget',
     
    },
    {
      title: 'विवरण',
      key: 'details',
      render: (text, record) => (
        <Link 
          to={`/planning/${record.id}/details`} 
          className="text-blue-500 hover:underline"
        >
          बिस्तृत विवरण
        </Link>
      ),
    },
  ];

  const rowClassName = (record, index) => {
    return index % 2 === 0 ? 'odd-row' : 'even-row';
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <div className="flex flex-1 pt-20 px-4 md:px-10">
        <Sidebar />
        <div className="w-full md:ml-56 p-4">
          <h1 className="text-2xl font-semibold mb-4 pt-10">योजनाको सुची</h1>

          {/* Search and Filter Section */}
          <div className="flex flex-col md:flex-row gap-4 mb-6 items-center justify-between">
            {/* Search Input */}
            <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
              <Select
                value={searchField}
                onChange={(value) => setSearchField(value)}
                className="w-full md:w-28"
                size="large"
              >
                <Option value="title">सिर्षक</Option>
                <Option value="budget">रकम</Option>
              </Select>
              <Input
                placeholder="खोज..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                className="flex-1 w-full md:w-48"
                size="large"
              />
              <Button 
                type="primary" 
                size="large" 
                onClick={searchPlanningDocuments}
                className="w-full md:w-auto"
              >
                खोज
              </Button>
            </div>

            {/* Sorting and Filtering */}
            <div className="flex flex-col md:flex-row items-center gap-2 w-full md:w-auto">
              {/* Sort By */}
              <div className="flex items-center gap-2">
                <label className="whitespace-nowrap">Sort By:</label>
                <Select
                  onChange={(value) => setSortBy(value)}
                  value={sortBy}
                  className="w-full md:w-32"
                >
                  <Option value="id">क्र.सं.</Option>
                  <Option value="budget">रकम</Option>
                </Select>
              </div>

              {/* Sort Order */}
              <div className="flex items-center gap-2">
                <label className="whitespace-nowrap">Order:</label>
                <Select
                  onChange={(value) => setSortOrder(value)}
                  value={sortOrder}
                  className="w-full md:w-32"
                >
                  <Option value="asc">Ascending</Option>
                  <Option value="desc">Descending</Option>
                </Select>
              </div>

              {/* Fiscal Year */}
              <div className="flex items-center gap-2">
                <label className="whitespace-nowrap">Fiscal Year:</label>
                <Select
                  onChange={(value) => setFiscalYear(value)}
                  value={fiscalYear}
                  className="w-full md:w-32"
                >
                  {fiscalYears.map((fy) => (
                    <Option key={fy.id} value={fy.id}>
                      {fy.name}
                    </Option>
                  ))}
                </Select>
              </div>

              {/* Add Button */}
              <Button
                type="primary"
                onClick={() => navigate("/planning/add")}
                className="w-full md:w-auto"
              >
                Add
              </Button>
            </div>
          </div>

          {/* Table Section */}
          <div className="overflow-x-auto">
            <Table
              dataSource={planningDocuments}
              columns={columns}
              rowClassName={rowClassName}
              pagination={false}
              bordered
              className="custom-table w-full"
            />
          </div>

          {/* Pagination Controls */}
          <div className="flex justify-end mt-4 gap-2">
            <Button 
              onClick={() => handlePageChange(page - 1)} 
              disabled={page === 0}
            >
              Previous
            </Button>
            <Button 
              onClick={() => handlePageChange(page + 1)} 
              disabled={page >= totalPages - 1}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanningDocumentList;